import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Stethoscope, Calendar, Clock, Phone, Shield, Heart, Users, ArrowLeft, CheckCircle2, Star, Video, FileText, Bell, User, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const MedCareDemo = () => {
  const [selectedDate, setSelectedDate] = useState('');
  
  const handleAppointment = (e) => {
    e.preventDefault();
    toast.success('✅ Termin erfolgreich gebucht!', {
      description: 'Bestätigung wurde per E-Mail versendet.'
    });
  };

  const services = [
    { icon: Stethoscope, title: 'Allgemeinmedizin', desc: 'Umfassende hausärztliche Versorgung' },
    { icon: Heart, title: 'Vorsorge', desc: 'Check-ups und Präventionsuntersuchungen' },
    { icon: Video, title: 'Videosprechstunde', desc: 'Bequem von zuhause aus' },
    { icon: FileText, title: 'Digitale Rezepte', desc: 'Direkt aufs Smartphone' },
  ];

  const doctors = [
    { name: 'Dr. med. Michael Schmidt', specialty: 'Allgemeinmedizin', img: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&q=80', available: true },
    { name: 'Dr. med. Anna Müller', specialty: 'Innere Medizin', img: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&q=80', available: true },
    { name: 'Dr. med. Thomas Weber', specialty: 'Kardiologie', img: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=200&q=80', available: false },
  ];

  const timeSlots = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '14:00', '14:30', '15:00', '15:30', '16:00'];

  const testimonials = [
    { name: 'Maria K.', text: 'Endlich eine Praxis mit Online-Terminen! Sehr modern und trotzdem persönlich.', rating: 5 },
    { name: 'Peter S.', text: 'Die Videosprechstunde hat mir sehr geholfen. Top Service!', rating: 5 },
  ];

  return (
    <div className="min-h-screen bg-sky-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white shadow-md">
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Portfolio
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
              <Stethoscope className="text-white" size={20} />
            </div>
            <div>
              <span className="text-slate-900 font-bold">Dr. Schmidt & Partner</span>
              <span className="text-sky-600 text-xs block">Praxis für Allgemeinmedizin</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#leistungen" className="hover:text-sky-600">Leistungen</a>
            <a href="#team" className="hover:text-sky-600">Team</a>
            <a href="#termin" className="hover:text-sky-600">Termin</a>
            <a href="#kontakt" className="hover:text-sky-600">Kontakt</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="hidden sm:flex">
              <User size={16} className="mr-2" />
              Patientenportal
            </Button>
            <Button className="bg-sky-600 hover:bg-sky-700 text-white">
              <Calendar size={16} className="mr-2" />
              Termin buchen
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-sky-600 to-sky-800 pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=1920&q=80')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 mb-6">
                <Shield className="text-white" size={16} />
                <span className="text-white text-sm font-medium">DSGVO-konform & sicher</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
                Ihre Gesundheit in besten Händen
              </h1>
              <p className="text-xl text-sky-100 mb-8 leading-relaxed">
                Moderne Allgemeinmedizin mit persönlicher Betreuung. Online-Termine, digitale Rezepte und sichere Videosprechstunden.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 px-8">
                  <Calendar size={18} className="mr-2" />
                  Online Termin buchen
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Phone size={18} className="mr-2" />
                  030 987 654
                </Button>
              </div>
              
              {/* Trust */}
              <div className="mt-12 flex flex-wrap gap-6">
                {[
                  { icon: Clock, text: 'Termine in 24h' },
                  { icon: Video, text: 'Videosprechstunde' },
                  { icon: Shield, text: 'Alle Kassen' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-white">
                    <item.icon size={18} className="text-sky-200" />
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Booking Card */}
            <Card className="shadow-2xl">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Schnell-Terminbuchung</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Datum wählen</label>
                    <Input type="date" className="bg-slate-50" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-2 block">Uhrzeit wählen</label>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.slice(0, 8).map((slot, i) => (
                        <button key={i} className="py-2 text-sm border border-slate-200 rounded-lg hover:border-sky-500 hover:bg-sky-50 transition-colors">
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 mb-1 block">Anliegen</label>
                    <select className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm">
                      <option>Allgemeine Untersuchung</option>
                      <option>Vorsorge / Check-up</option>
                      <option>Akute Beschwerden</option>
                      <option>Rezept / Überweisung</option>
                      <option>Videosprechstunde</option>
                    </select>
                  </div>
                  <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    Termin bestätigen
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="leistungen" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Unsere Leistungen</span>
            <h2 className="text-3xl font-bold text-slate-900 mt-3">Umfassende medizinische Versorgung</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <Card key={i} className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-sky-600 transition-colors">
                    <service.icon className="text-sky-600 group-hover:text-white transition-colors" size={32} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Unser Team</span>
            <h2 className="text-3xl font-bold text-slate-900 mt-3">Ihre Ärzte</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {doctors.map((doctor, i) => (
              <Card key={i} className="border-0 shadow-lg overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img src={doctor.img} alt={doctor.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-slate-900">{doctor.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${doctor.available ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {doctor.available ? 'Verfügbar' : 'Ausgebucht'}
                    </span>
                  </div>
                  <p className="text-sky-600 text-sm mb-4">{doctor.specialty}</p>
                  <Button variant="outline" className="w-full border-sky-600 text-sky-600 hover:bg-sky-50" disabled={!doctor.available}>
                    Termin buchen
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Das sagen unsere Patienten</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4">"{t.text}"</p>
                  <div className="font-semibold text-slate-900">{t.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="kontakt" className="py-20 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Kontakt & Anfahrt</h2>
              <div className="space-y-4">
                <p className="flex items-center gap-3">
                  <Phone size={20} />
                  030 987 654
                </p>
                <p className="flex items-center gap-3">
                  <Clock size={20} />
                  Mo-Fr: 08:00-18:00, Sa: 09:00-12:00
                </p>
                <p className="flex items-start gap-3">
                  <Calendar size={20} className="mt-1" />
                  <span>Gesundheitsstraße 15<br />10115 Berlin</span>
                </p>
              </div>
            </div>
            <Card className="shadow-xl">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Nachricht senden</h3>
                <form className="space-y-4">
                  <Input placeholder="Ihr Name" className="bg-slate-50" />
                  <Input type="email" placeholder="Ihre E-Mail" className="bg-slate-50" />
                  <textarea className="w-full h-24 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg resize-none" placeholder="Ihre Nachricht..."></textarea>
                  <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    Absenden
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-slate-500 text-sm">
          © 2024 Dr. Schmidt & Partner. Alle Rechte vorbehalten. | Impressum | Datenschutz
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        Demo-Projekt von Beta-Webagentur
      </div>
    </div>
  );
};

export default MedCareDemo;
